import { ChartData, ChartOptions, Plugin } from "chart.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ColorConstants } from "../../../constants";
import { thousandsFormatter } from "../../../utils";
import { PieChartCenterTextFont, PieChartColors } from "../models";
import {
  ChartTheme,
  getChartColours,
  getChartGradientColours,
  getChartGradientHighlightColours,
  getChartHighlightColours,
} from "../utils";

interface UsePieChartReturnData {
  chartData: ChartData<"doughnut", number[], string>;
  legendData: { [key: string]: number };
  chartColours: string[];
  plugins: Plugin<"doughnut">[] | undefined;
  options: ChartOptions<"doughnut">;
  hasData: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pieChartRef: (node: any) => void;
}

export const usePieChart = (
  data: {
    [key: string]: number;
  },
  centerTextPrefix: string | undefined,
  centerTextSuffix: string | undefined,
  centerTextHidden: boolean,
  centerTextFont: PieChartCenterTextFont,
  noDataMessage: string,
  isGradient: boolean,
  colors: PieChartColors,
  chartColorsOverride: string[][] | undefined,
  theme: ChartTheme
): UsePieChartReturnData => {
  const { arcBorderRadius } = theme;
  const hasData: boolean = useMemo(() => data && Object.values(data).find((x) => !!x) !== undefined, [data]);
  const [chartData, setChartData] = useState<ChartData<"doughnut", number[], string>>({ datasets: [] });
  const [legendData, setLegendData] = useState<{ [key: string]: number }>(data || { [noDataMessage]: 0 });
  const [chartColours, setChartColours] = useState<string[]>(
    chartColorsOverride ? chartColorsOverride.map((x) => x?.[0]) : []
  );
  const [chartNoDataColour] = useState<string>(ColorConstants.GREY_100);

  const [plugins, setPlugins] = useState<Plugin<"doughnut">[] | undefined>(undefined);

  /*
    react-chartjs-2 forwards its ref once, when the chart is created, and never again when the
    ref's identity changes. So the ref only records the chart instance: the gradient build lives in
    the effect below, keyed on `data`, otherwise a chart mounted with placeholder data (a card still
    loading) would keep drawing it after the real figures arrive.
  */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [chartNode, setChartNode] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pieChartRef = useCallback((node: any) => setChartNode(node), []);

  /*
    Drops the segment Chart.js remembers as hovered before its dataset is swapped. The chart replays
    that hover on its next update, and the chart's own effects run before PieChart's, so a dataset
    that shrank (populated -> the one-arc placeholder, or fewer segments) would have it restyle an
    element that no longer exists: "can't access property 'active', element is undefined".
  */
  const clearActiveElements = (): void => {
    if (chartNode?.ctx) chartNode.setActiveElements([]);
  };

  useEffect(
    () => {
      const node = chartNode;
      /*
        Only the gradients need the canvas. A destroyed chart keeps its object but nulls its context,
        and the replacement re-runs this via the ref; the flat no-data placeholder needs neither.
      */
      if (data && isGradient && (!hasData || node?.ctx)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const setGradients = (gradientColours: string[][]): any => {
          const { ctx } = node;

          const gradientsArr = [];
          const dataValues = Object.values(data);
          const sumValues = dataValues.reduce((sum, currentValue) => {
            return sum + currentValue;
          }, 0);

          for (let i = 0; i < gradientColours.length; i++) {
            let gradientAngle = Math.PI / 2;
            if (i > 0) {
              const angleRatio =
                dataValues.slice(0, i).reduce((sum, currentValue) => {
                  return sum + currentValue;
                }, 0) / sumValues;
              gradientAngle += 2 * Math.PI * angleRatio;
            }

            const gradient = ctx.createConicGradient(gradientAngle, node.height / 2, node.width / 2);
            gradient.addColorStop(0, gradientColours?.[i]?.[0] ?? ColorConstants.GREY_300);
            gradient.addColorStop(0.6, gradientColours?.[i]?.[1] ?? ColorConstants.GREY_50);

            gradientsArr.push(gradient);
          }

          return gradientsArr;
        };

        const xLabels = Object.keys(data);
        const dataPoints = xLabels.map((label) => data[label]);

        const chartColorsOverrideMainColors = chartColorsOverride?.map((x) => x?.[0]);

        const colours = hasData
          ? (chartColorsOverrideMainColors ?? getChartColours(dataPoints.length, colors))
          : [chartNoDataColour];
        const gradientColours = hasData
          ? (chartColorsOverride ?? getChartGradientColours(dataPoints.length, colors))
          : [[chartNoDataColour, chartNoDataColour]];
        const highlightColours = hasData
          ? (chartColorsOverrideMainColors ?? getChartGradientHighlightColours(dataPoints.length, colors))
          : [chartNoDataColour];
        clearActiveElements();
        setChartColours(colours);
        setChartData({
          labels: xLabels,
          datasets: [
            {
              data: hasData ? dataPoints : [1],
              backgroundColor: hasData ? setGradients(gradientColours) : chartNoDataColour,
              // Surface-coloured gap + rounded ends give each segment breathing room.
              borderColor: ColorConstants.WHITE,
              borderWidth: hasData ? 2 : 0,
              borderRadius: hasData ? arcBorderRadius : 0,
              hoverBackgroundColor: highlightColours,
              rotation: 180,
            },
          ],
        });
        setLegendData(hasData ? data : { [noDataMessage]: 0 });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, chartNode]
  );

  /*
    Chart.js is handed its plugins once, when the chart is created, so the centre-text plugin reads
    everything that can change from this ref. Captured in a closure instead, a chart mounted while
    still empty would keep seeing `hasData: false` and never draw its figure.
  */
  const centerTextSettings = useRef({ hasData, centerTextHidden, centerTextPrefix, centerTextSuffix, centerTextFont });
  centerTextSettings.current = { hasData, centerTextHidden, centerTextPrefix, centerTextSuffix, centerTextFont };

  useEffect(() => {
    if (data && Object.values(data).length) {
      setPlugins([
        {
          id: "text",
          beforeDraw: (chart) => {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const { hasData, centerTextHidden, centerTextPrefix, centerTextSuffix, centerTextFont } =
              centerTextSettings.current;
            if (centerTextHidden || !hasData) return;
            const { width, height, ctx } = chart;
            ctx.restore();
            const centerText =
              (centerTextPrefix || "") +
              thousandsFormatter
                .format(
                  Math.abs(
                    chart.data.datasets
                      .reduce<number[]>((a, b) => a.concat(b.data as number[]), [])
                      .reduce((a, b) => a + b, 0)
                  )
                )
                .toLowerCase() +
              (centerTextSuffix || "");
            // The centre figure is the card's headline either way — bold and indigo in
            // both brands, with only the typeface changing between them.
            const fontSize = (width / 110).toFixed(2);
            ctx.font = `Bold ${fontSize}em ${centerTextFont}, sans-serif`;
            ctx.fillStyle = `${ColorConstants.INDIGO_500}`;
            ctx.textBaseline = "alphabetic";

            // Centre the ink, not the em box: "middle" and the advance width both leave
            // digits sitting high in the hole, because they have no descender to balance
            // the ascent. Falls back to the em box where the metrics aren't available.
            const text = centerText?.toString() || "";
            const metrics = ctx.measureText(text);
            const inkLeft = metrics.actualBoundingBoxLeft ?? 0;
            const inkRight = metrics.actualBoundingBoxRight ?? metrics.width;
            const inkAscent = metrics.actualBoundingBoxAscent ?? 0;
            const inkDescent = metrics.actualBoundingBoxDescent ?? 0;

            const textX = width / 2 - (inkRight - inkLeft) / 2 + inkLeft;
            const textY = height / 2 + (inkAscent - inkDescent) / 2;

            ctx.fillText(text, textX, textY);
            ctx.save();
          },
        },
      ]);
    }
  }, [data]);
  const options: ChartOptions<"doughnut"> = useMemo(
    () => ({
      cutout: isGradient ? "65%" : "50%",
      animation: {
        duration: 0,
      },
      // No inset: the donut fills its box, so the only spacing around it is the card's
      // padding and the gap to the key.
      layout: { padding: 0 },
      devicePixelRatio: 4, // Use 4 for clarity
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          // Drawn as DOM by PieChart instead: a canvas tooltip is clipped by the canvas,
          // which on a 120px donut cuts off any name longer than the chart itself.
          enabled: false,
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, theme]
  );

  useEffect(() => {
    if (!data || isGradient) return;

    const xLabels = Object.keys(data);
    const dataPoints = xLabels.map((label) => data[label]);
    const chartColorsOverrideMainColors = chartColorsOverride?.map((x) => x[0]);

    const colours = hasData
      ? (chartColorsOverrideMainColors ?? getChartColours(dataPoints.length, colors))
      : [chartNoDataColour];
    const highlightColours = hasData
      ? (chartColorsOverrideMainColors ?? getChartHighlightColours(dataPoints.length, colors))
      : [chartNoDataColour];
    clearActiveElements();
    setChartColours(colours);
    setChartData({
      labels: xLabels,
      datasets: [
        {
          data: hasData ? dataPoints : [1],
          backgroundColor: hasData ? colours : chartNoDataColour,
          // Surface-coloured gap + rounded ends give each segment breathing room.
          borderColor: ColorConstants.WHITE,
          borderWidth: hasData ? 2 : 0,
          borderRadius: hasData ? arcBorderRadius : 0,
          hoverBackgroundColor: highlightColours,
        },
      ],
    });
    setLegendData(hasData ? data : { [noDataMessage]: 0 });
  }, [data]);

  return {
    chartData,
    legendData,
    chartColours,
    plugins,
    options,
    hasData,
    pieChartRef,
  };
};
